import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Subject, Observable, defer, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, map, switchMap } from 'rxjs/operators';
import { OfferSetListService } from '../../../../service/offer-set-list.service';
import { OfferSetListItem } from '../../../../model/offer-set-list-item.model';
import { OfferSetListCounts, OfferSetListPagination, OfferSetListResponse } from '../../../../model/offer-set-list-response.model';
import { OfferSetBulkActionRequest, OfferSetBulkActionResponse, ProtectedOfferSetBulkActionResponse } from '../../../../model/offer-set-bulk-action.model';
import { environment } from '../../../../../environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { OfferSetRegistryService } from '../../../../service/offer-set-registry-service';
import {OfferSetPublicOffersDto, OfferSetPublicOffersService} from '../../../../service/offer-set-public-offers.service';
import {UserService} from '../../../../service/user.service';
import {VehicleService} from '../../../../service/vehicle.service';
import {MakeModel} from '../../../../model/make.model';
import {VehicleModel} from '../../../../model/vehicle-model.model';
import {OfferSetCreateRequest} from '../../../../model/offer-set-create.model';
import {CreateAdFromStockRequest, CreateAdFromStockResponse} from '../../../../model/create-ad-from-stock.model';
import {Menu} from 'primeng/menu';

const linkedDeletionProtectionFeature = 'linkedDeletionProtection';
const linkedRuleGuidance = 'This Offer Set is managed by an IPOP Rule and must be deleted from '
  + 'Settings > IPOP > Offer Set Rules.';
const deletionProtectionUnavailable = 'Unable to verify deletion protection. Reload the list and try again.';
const protectedOfferSetCode = 'IPOP_RULE_OWNED_OFFER_SET';
const protectedBulkActions = ['activate', 'inactivate', 'delete'];
const protectedResultStatuses = ['success', 'partial_success', 'failed'];
const protectedSelectionPageSize = 100;
const protectionMessageLifetime = 5000;

interface ProtectedListOperation {
  request: OfferSetBulkActionRequest;
  selectionContext: string;
  single: boolean;
}

interface PresetTab {
  label: string;
  preset: string;
  queryValue: string;
  status: string;
  condition: string;
  visibility: string;
}

interface FilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'auto-offer-set-list',
  templateUrl: './offer-set-list.component.html',
  styleUrls: ['./offer-set-list.component.scss'],
  standalone: false
})
export class OfferSetListComponent implements OnInit, OnDestroy {
  private readonly tabQueryParam = 'tab';
  private readonly firstPage = 1;
  private tabQueryValue: string | null | undefined;

  accountId: number;
  items: OfferSetListItem[] = [];
  pagination: OfferSetListPagination = {
    page: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0
  };
  counts: OfferSetListCounts = {
    presets: {},
    visibility: {}
  };
  isLoading = false;
  selectedItems: OfferSetListItem[] = [];
  allSelected = false;
  selectAllAcrossPages = false;
  searchTerm = '';
  activePreset = 'new_active';
  selectedBulkAction = '';
  selectedStatus = 'active';
  selectedCondition = 'new';
  selectedVisibility = 'full_visibility';
  enableNewOfferSetCreationModal = false;
  inventoryMenuItems: MenuItem[] = [
    {
      label: 'New Inventory',
      command: () => this.viewInventory('new')
    },
    {
      label: 'Used Inventory',
      command: () => this.viewInventory('used')
    }
  ];

  // Disclosure dialog state
  disclosureDialogVisible = false;
  disclosureTitle = '';
  disclosureHtml: SafeHtml | string = '';

  // Build New Offer Set dialog state
  private readonly conditionNew = 'New';
  private readonly conditionLoaner = 'Loaner';
  private readonly stockConditionNew = 'new';
  private readonly stockConditionUsed = 'used';
  private readonly mismatchRedirectDelayMs = 2500;
  private readonly yearOptionsFutureOffset = 4;
  private readonly newVehicleStartYear = 2014;
  private readonly usedVehicleStartYear = 2000;
  buildDialogVisible = false;
  manualDialogVisible = false;
  stockDialogVisible = false;
  isCreatingFromStock = false;
  makes: MakeModel[] = [];
  models: VehicleModel[] = [];
  filteredModels: VehicleModel[] = [];
  yearOptions: number[] = this.buildYearOptions('new');
  newOfferSetCondition = this.conditionNew;
  newOfferSetYear: number | null = null;
  newOfferSetMakeId: number | null = null;
  newOfferSetModelId: number | null = null;
  newOfferSetTrim = '';
  newOfferSetStockNumber = '';
  buildConditionOptions: FilterOption[] = [
    {label: this.conditionNew, value: this.conditionNew},
    {label: this.conditionLoaner, value: this.conditionLoaner}
  ];

  publicOffersByOfferSetId: Record<number, OfferSetPublicOffersDto> = {};

  presetTabs: PresetTab[] = [
    { label: 'New · Active', preset: 'new_active', queryValue: 'newActive', status: 'active', condition: 'new', visibility: 'full_visibility' },
    { label: 'New · Inactive', preset: 'new_inactive', queryValue: 'newInactive', status: 'inactive', condition: 'new', visibility: 'full_visibility' },
    { label: 'Used · Active', preset: 'used_active', queryValue: 'usedActive', status: 'active', condition: 'used', visibility: 'full_visibility' },
    { label: 'Used · Inactive', preset: 'used_inactive', queryValue: 'usedInactive', status: 'inactive', condition: 'used', visibility: 'full_visibility' },
    { label: 'All Offer Sets', preset: 'all', queryValue: 'all', status: 'any', condition: 'any', visibility: 'any' }
  ];

  statusOptions: FilterOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Any', value: 'any' }
  ];

  conditionOptions: FilterOption[] = [
    { label: 'New', value: 'new' },
    { label: 'Used', value: 'used' },
    { label: 'Any', value: 'any' }
  ];

  visibilityOptions: FilterOption[] = [
    { label: 'Full Visibility', value: 'full_visibility' },
    { label: 'Inventory Only', value: 'inventory_only' },
    { label: 'Any', value: 'any' }
  ];

  bulkActionOptions: FilterOption[] = [
    { label: 'Bulk Action: Select', value: '' },
    { label: 'Activate', value: 'activate' },
    { label: 'Inactivate', value: 'inactivate' },
    { label: 'Delete', value: 'delete' }
  ];

  pageSizeOptions: FilterOption[] = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
  ];

  linkedDeletionProtection: boolean | null = null;
  protectionGuidanceById: Record<number, string> = {};
  private protectedRowsReady = false;
  private protectedLoadSequence = 0;
  private selectionRevision = 0;
  private protectionDestroyed = false;
  private protectedMutationPending = false;
  private isGatheringProtectedIds = false;
  private protectedAcrossCount: number | null = null;
  private protectedSelectionCancelled = new Subject<void>();

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  /**
   * Excludes shared rows and requires verified metadata when protection is enabled.
   */
  isSelectable(item: OfferSetListItem): boolean
  {
    let selectable = !item.isShared;
    if (this.linkedDeletionProtection !== false) {
      selectable = selectable && this.linkedDeletionProtection === true && this.protectedRowsReady
        && item.ipopRuleDeletionProtected === false;
    }

    return selectable;
  }

  /**
   * Filters the current rows to offers eligible for bulk selection.
   */
  private getSelectableItems(items: OfferSetListItem[]): OfferSetListItem[] {
    return (items || []).filter((item) => this.isSelectable(item));
  }

  /**
   * Supplies the eligible row count used by page-selection controls.
   */
  getSelectableItemCount(): number {
    const selectableItems = this.getSelectableItems(this.items);

    return selectableItems.length;
  }

  /**
   * Compares selection against eligible rows rather than every displayed row.
   */
  areAllSelectableItemsSelected(): boolean {
    const selectableCount = this.getSelectableItemCount();
    const allSelected = selectableCount > 0 && this.selectedItems.length === selectableCount;

    return allSelected;
  }

  /**
   * Binds list navigation, transport and action feedback services.
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private offerSetListService: OfferSetListService,
    private offerRegistryService: OfferSetRegistryService,
    private offerSetPublicOffersService: OfferSetPublicOffersService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private vehicleService: VehicleService
  )
  {}

  /**
   * Initializes account and feature state before loading the offer list.
   */
  ngOnInit(): void
  {
    this.accountId = Number(this.route.snapshot.paramMap.get('accountId'));
    this.initSearchDebounce();
    this.initTabQueryParam();
    this.loadNewOfferSetCreationModalFeatureFlag();
    this.loadLinkedDeletionProtection();
  }

  /**
   * Restores the selected preset from navigation query parameters.
   */
  private initTabQueryParam(): void
  {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((queryParams) => {
      const requestedPreset = queryParams.get(this.tabQueryParam);

      if (requestedPreset !== this.tabQueryValue) {
        const defaultTabIndex = 0;
        const tab = this.presetTabs.find((presetTab) => presetTab.queryValue === requestedPreset)
          ?? this.presetTabs[defaultTabIndex];
        this.tabQueryValue = requestedPreset;
        this.applyPreset(tab);
      }
    });
  }

  /**
   * Keeps navigation state aligned with the selected offer-list preset.
   */
  private updateTabQueryParam(preset: string | null): void
  {
    this.tabQueryValue = preset;

    if (this.route.snapshot.queryParamMap.get(this.tabQueryParam) !== preset) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {[this.tabQueryParam]: preset},
        queryParamsHandling: 'merge',
        preserveFragment: true
      });
    }
  }

  /**
   * Resolves the feature decision before exposing protected actions.
   */
  private loadLinkedDeletionProtection(): void
  {
    this.userService.getMpopFeature(linkedDeletionProtectionFeature).pipe(
      map((features) =>
      {
        if (!Array.isArray(features) || features.some((feature) => !feature
          || typeof feature.name !== 'string' || typeof feature.enabled !== 'boolean')) {
          throw new Error(deletionProtectionUnavailable);
        }

        return features.some((feature) => feature.name === linkedDeletionProtectionFeature && feature.enabled);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (enabled) =>
      {
        this.linkedDeletionProtection = enabled;
        if (enabled) {
          this.watchProtectedAccount();
        }
        this.loadOfferSets();
      },
      error: () =>
      {
        this.isLoading = false;
        this.showProtectionMessage(deletionProtectionUnavailable);
      }
    });
  }

  /**
   * Invalidates protected list and selection state when the account changes.
   */
  private watchProtectedAccount(): void
  {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) =>
    {
      const accountId = Number(params.get('accountId'));
      if (accountId !== this.accountId) {
        this.accountId = accountId;
        this.items = [];
        this.pagination.page = this.firstPage;
        this.clearSelection();
        this.loadOfferSets();
      }
    });
  }

  /**
   * Displays generic rule-management guidance for a protected action.
   */
  private showProtectionMessage(detail: string, severity = 'error'): void
  {
    this.messageService.add({severity, summary: 'Offer Sets', detail, life: protectionMessageLifetime});
  }

  /**
   * Advances the context revision so stale collection or confirmation work is rejected.
   */
  onProtectedSelectionChange(): void
  {
    if (this.linkedDeletionProtection !== false) {
      this.selectionRevision++;
      this.protectedSelectionCancelled.next();
      this.isGatheringProtectedIds = false;
      this.protectedAcrossCount = null;
    }
  }

  /**
   * Captures account, filters, action and selection identity for stale-work checks.
   */
  private getSelectionContext(): string
  {
    const context = [this.accountId, this.activePreset, this.selectedStatus, this.selectedCondition,
      this.selectedVisibility, this.searchTerm, this.pagination.page, this.pagination.pageSize,
      this.selectedBulkAction, this.selectAllAcrossPages, this.selectionRevision,
      this.selectedItems.map((item) => item.id).sort((left, right) => left - right)];

    return JSON.stringify(context);
  }

  /**
   * Requires the originating selection context to remain current and usable.
   */
  private isCurrentSelection(context: string): boolean
  {
    const isCurrent = !this.protectionDestroyed && this.linkedDeletionProtection === true
      && this.protectedRowsReady && context === this.getSelectionContext();

    return isCurrent;
  }

  /**
   * Blocks actions while feature, row or operation state is unresolved.
   */
  isProtectedActionUnavailable(): boolean
  {
    const unavailable = this.linkedDeletionProtection !== false
      && (!this.protectedRowsReady || this.isGatheringProtectedIds || this.protectedMutationPending);

    return unavailable;
  }

  /**
   * Reports selected versus eligible rows for the active protection mode.
   */
  getPageSelectionText(): string
  {
    let text = `All ${this.items.length} items on this page are selected.`;
    if (this.linkedDeletionProtection === true) {
      const eligibleCount = this.getSelectableItemCount();
      text = `All ${eligibleCount} eligible items on this page are selected.`;
      if (eligibleCount === 0) {
        text = 'No eligible Offer Sets on this page.';
      }
    }

    return text;
  }

  /**
   * Allows across-page selection when more eligible candidates may exist.
   */
  canOfferAcrossSelection(): boolean
  {
    const selectableCount = this.getSelectableItemCount();
    let available = this.areAllSelectableItemsSelected() && this.pagination.totalItems > selectableCount;
    if (this.linkedDeletionProtection === true && this.protectedRowsReady && selectableCount === 0) {
      available = this.pagination.totalItems > this.items.length;
    }

    return available;
  }

  /**
   * Chooses the selection action text for the current across-page mode.
   */
  getAcrossSelectionLink(): string
  {
    let text = `Select all ${this.pagination.totalItems} results`;
    if (this.linkedDeletionProtection === true) {
      text = 'Select eligible results across all pages';
    }

    return text;
  }

  /**
   * Explains whether across-page eligibility is pending or has a confirmed count.
   */
  getAcrossSelectionText(): string
  {
    let text = `All ${this.pagination.totalItems} results are selected across all pages.`;
    if (this.linkedDeletionProtection === true) {
      text = 'Eligible results across all pages will be checked when you apply the action.';
      if (this.protectedAcrossCount !== null) {
        text = `${this.protectedAcrossCount} eligible results are selected across all pages.`;
      }
    }

    return text;
  }

  /**
   * Loads protected rows with request ownership so stale responses cannot replace the view.
   */
  private loadProtectedOfferSets(): void
  {
    this.onProtectedSelectionChange();
    const loadSequence = ++this.protectedLoadSequence;
    const accountId = this.accountId;
    this.protectedRowsReady = false;
    this.isLoading = true;
    this.requestProtectedPage(this.pagination.page, this.pagination.pageSize).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) =>
      {
        if (this.isCurrentProtectedLoad(loadSequence, accountId)) {
          this.acceptProtectedList(response, loadSequence, accountId);
        }
      },
      error: () =>
      {
        if (this.isCurrentProtectedLoad(loadSequence, accountId)) {
          this.rejectProtectedList();
        }
      }
    });
  }

  /**
   * Validates account, pagination and metadata before accepting a selection page.
   */
  private requestProtectedPage(page: number, pageSize: number): Observable<OfferSetListResponse>
  {
    const accountId = this.accountId;
    const response = this.offerSetListService.getOfferSetList(accountId, this.activePreset,
      this.selectedStatus, this.selectedCondition, this.selectedVisibility, this.searchTerm, page, pageSize)
      .pipe(map((result) =>
      {
        this.validateProtectedPage(result, accountId, page, pageSize);

        return result;
      }));

    return response;
  }

  /**
   * Rejects malformed, incomplete or duplicate rows before they can enter a bulk action.
   */
  private validateProtectedPage(result: OfferSetListResponse, accountId: number, page: number, pageSize: number): void
  {
    const pagination = result?.pagination;
    const validPagination = pagination && pagination.page === page && pagination.pageSize === pageSize
      && this.isCount(pagination.totalItems) && this.isCount(pagination.totalPages)
      && pagination.totalPages === Math.ceil(pagination.totalItems / pageSize);
    const validItems = Array.isArray(result?.items) && result.items.every((item) => item
      && Number.isSafeInteger(item.id) && item.id > 0 && typeof item.isShared === 'boolean'
      && typeof item.ipopRuleDeletionProtected === 'boolean' && this.isRecord(item.data));
    if (!validPagination || result?.accountId !== accountId || !validItems) {
      throw new Error(deletionProtectionUnavailable);
    }
    const expectedItems = Math.min(pageSize, Math.max(0, pagination.totalItems - (page - 1) * pageSize));
    if (result.items.length !== expectedItems || new Set(result.items.map((item) => item.id)).size !== expectedItems) {
      throw new Error(deletionProtectionUnavailable);
    }
  }

  /**
   * Checks that a response still belongs to the active account and load sequence.
   */
  private isCurrentProtectedLoad(loadSequence: number, accountId: number): boolean
  {
    const isCurrent = !this.protectionDestroyed && loadSequence === this.protectedLoadSequence
      && accountId === this.accountId;

    return isCurrent;
  }

  /**
   * Reconciles eligible selection with fresh rows without clearing a newer selection.
   */
  private acceptProtectedList(response: OfferSetListResponse, loadSequence: number, accountId: number): void
  {
    this.items = response.items.map((item) => ({
      ...item, isMissingMakeOrModel: this.computeIsMissingMakeOrModel(item)
    }));
    this.pagination = response.pagination;
    this.counts = response.counts ?? {presets: {}, visibility: {}};
    this.protectedRowsReady = true;
    this.isLoading = false;
    this.protectionGuidanceById = {};
    for (const item of this.items) {
      if (item.ipopRuleDeletionProtected) {
        this.protectionGuidanceById[item.id] = linkedRuleGuidance;
      }
    }
    const selectedIds = new Set(this.selectedItems.map((item) => item.id));
    this.selectedItems = this.getSelectableItems(this.items).filter((item) => selectedIds.has(item.id));
    this.allSelected = this.areAllSelectableItemsSelected();
    this.loadProtectedPublicOffers(loadSequence, accountId);
    this.syncProtectedRegistry(loadSequence, accountId);
  }

  /**
   * Removes stale eligibility and presents a retryable protected-list failure.
   */
  private rejectProtectedList(detail = deletionProtectionUnavailable): void
  {
    this.items = [];
    this.protectionGuidanceById = {};
    this.publicOffersByOfferSetId = {};
    this.protectedRowsReady = false;
    this.isLoading = false;
    this.clearSelection();
    this.showProtectionMessage(detail);
  }

  /**
   * Applies public-offer enrichment only to the list request that initiated it.
   */
  private loadProtectedPublicOffers(loadSequence: number, accountId: number): void
  {
    this.publicOffersByOfferSetId = {};
    const ids = this.items.map((item) => item.id);
    if (ids.length > 0) {
      this.offerSetPublicOffersService.getPublicOffersByOfferSetIds(ids).pipe(takeUntil(this.destroy$)).subscribe({
        next: (offers) =>
        {
          if (this.isCurrentProtectedLoad(loadSequence, accountId)) {
            this.publicOffersByOfferSetId = this.buildPublicOffersLookup(offers);
          }
        },
        error: () =>
        {
          if (this.isCurrentProtectedLoad(loadSequence, accountId)) {
            this.publicOffersByOfferSetId = {};
            this.showProtectionMessage('Failed to load public Offer Set information.');
          }
        }
      });
    }
  }

  /**
   * Scopes registry refresh completion to the currently displayed account.
   */
  private syncProtectedRegistry(loadSequence: number, accountId: number): void
  {
    this.offerRegistryService.callOfferRegistryService(accountId).pipe(takeUntil(this.destroy$)).subscribe({
      error: () =>
      {
        if (this.isCurrentProtectedLoad(loadSequence, accountId)) {
          this.showProtectionMessage('Failed to sync Offer Sets.');
        }
      }
    });
  }

  /**
   * Requires an eligible current row before creating a single-delete operation.
   */
  private beginProtectedSingleDelete(item: OfferSetListItem): void
  {
    const currentItem = this.items.find((row) => row.id === item.id);
    if (currentItem && this.isSelectable(currentItem) && !this.isProtectedActionUnavailable()) {
      const operation = this.createProtectedOperation('delete', [currentItem.id], true);
      this.confirmProtectedDelete(operation, `Are you sure you want to delete "${currentItem.data.displayTitle}"?`);
    } else {
      this.showProtectionMessage(this.protectionGuidanceById[item.id] ?? deletionProtectionUnavailable);
    }
  }

  /**
   * Freezes unique target IDs and their selection context for later validation.
   */
  private createProtectedOperation(action: string, ids: number[], single: boolean): ProtectedListOperation
  {
    const operation = {
      request: {accountId: this.accountId, action, offerSetIds: [...new Set(ids)]},
      selectionContext: this.getSelectionContext(), single
    };

    return operation;
  }

  /**
   * Starts a guarded bulk action from the current eligible selection.
   */
  private beginProtectedBulkAction(): void
  {
    if (!this.isProtectedActionUnavailable() && protectedBulkActions.includes(this.selectedBulkAction)) {
      if (this.selectAllAcrossPages) {
        this.gatherProtectedSelection();
      } else {
        const ids = this.selectedItems.map((item) => item.id);
        const operation = this.createProtectedOperation(this.selectedBulkAction, ids, false);
        this.prepareProtectedOperation(operation);
      }
    } else {
      this.showProtectionMessage(deletionProtectionUnavailable);
    }
  }

  /**
   * Collects all eligible targets before presenting the final action confirmation.
   */
  private gatherProtectedSelection(): void
  {
    const operation = this.createProtectedOperation(this.selectedBulkAction, [], false);
    this.isGatheringProtectedIds = true;
    const ids = new Set<number>();
    this.collectProtectedPage(operation.selectionContext, this.firstPage, ids).pipe(
      takeUntil(this.protectedSelectionCancelled), takeUntil(this.destroy$)
    ).subscribe({
      next: (gatheredIds) =>
      {
        if (this.isCurrentSelection(operation.selectionContext)) {
          this.isGatheringProtectedIds = false;
          this.protectedAcrossCount = gatheredIds.length;
          operation.request.offerSetIds = gatheredIds;
          this.prepareProtectedOperation(operation);
        }
      },
      error: () =>
      {
        if (this.isCurrentSelection(operation.selectionContext)) {
          this.isGatheringProtectedIds = false;
          this.rejectProtectedList('Unable to check every page. No bulk action was submitted. Reload and try again.');
        }
      }
    });
  }

  /**
   * Rejects stale or inconsistent pages while gathering eligible IDs across the list.
   */
  private collectProtectedPage(context: string, page: number, ids: Set<number>, expectedTotal?: number): Observable<number[]>
  {
    const response = defer(() =>
    {
      if (!this.isCurrentSelection(context)) {
        throw new Error(deletionProtectionUnavailable);
      }

      return this.requestProtectedPage(page, protectedSelectionPageSize);
    }).pipe(switchMap((result) =>
    {
      if (!this.isCurrentSelection(context)
        || (expectedTotal !== undefined && result.pagination.totalItems !== expectedTotal)) {
        throw new Error(deletionProtectionUnavailable);
      }
      this.getSelectableItems(result.items).forEach((item) => ids.add(item.id));
      let nextPage: Observable<number[]>;
      if (page < result.pagination.totalPages) {
        nextPage = this.collectProtectedPage(context, page + 1, ids, result.pagination.totalItems);
      } else {
        nextPage = of([...ids]);
      }

      return nextPage;
    }));

    return response;
  }

  /**
   * Revalidates collected targets before confirmation or non-delete submission.
   */
  private prepareProtectedOperation(operation: ProtectedListOperation): void
  {
    if (this.validateProtectedOperation(operation)) {
      if (operation.request.action === 'delete') {
        this.confirmProtectedDelete(operation,
          `Are you sure you want to delete ${operation.request.offerSetIds.length} eligible Offer Set(s)?`);
      } else {
        this.submitProtectedOperation(operation);
      }
    }
  }

  /**
   * Confirms the actual eligible count and rechecks context before deletion.
   */
  private confirmProtectedDelete(operation: ProtectedListOperation, message: string): void
  {
    let key = 'bulkDeleteOfferSets';
    if (operation.single) {
      key = 'deleteOfferSet';
    }
    this.confirmationService.confirm({
      key, defaultFocus: 'none', message, header: 'Delete Offer Sets',
      rejectButtonProps: {label: 'Cancel', severity: 'secondary'},
      acceptButtonProps: {label: 'Delete', severity: 'danger'},
      accept: () => this.submitProtectedOperation(operation),
      reject: () => this.confirmationService.close()
    });
  }

  /**
   * Rejects operations whose account, action or selection no longer matches the view.
   */
  private validateProtectedOperation(operation: ProtectedListOperation): boolean
  {
    const ids = operation.request.offerSetIds;
    const visibleRows = new Map(this.items.map((item) => [item.id, item]));
    const selectionIsValid = ids.every((id) =>
    {
      const row = visibleRows.get(id);
      let rowIsEligible = this.selectAllAcrossPages && !operation.single;
      if (row) {
        rowIsEligible = this.isSelectable(row);
      }

      return rowIsEligible;
    });
    const valid = this.isCurrentSelection(operation.selectionContext) && !this.protectedMutationPending
      && ids.length > 0 && selectionIsValid && operation.request.accountId === this.accountId;
    if (!valid) {
      this.showProtectionMessage('No eligible selection is available or the selection changed. Select Offer Sets again.');
    }

    return valid;
  }

  /**
   * Submits frozen action targets and handles success and error envelopes consistently.
   */
  private submitProtectedOperation(operation: ProtectedListOperation): void
  {
    if (this.validateProtectedOperation(operation)) {
      this.protectedMutationPending = true;
      this.offerSetListService.executeBulkAction(operation.request).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => this.completeProtectedOperation(operation, response),
        error: (error: unknown) => this.completeProtectedOperation(operation, error)
      });
    }
  }

  /**
   * Refreshes the applicable list while preserving newer selection ownership.
   */
  private completeProtectedOperation(operation: ProtectedListOperation, result: unknown): void
  {
    this.protectedMutationPending = false;
    if (!this.protectionDestroyed && operation.request.accountId === this.accountId) {
      this.presentProtectedResult(operation, result);
      if (this.isCurrentSelection(operation.selectionContext)) {
        this.selectedBulkAction = '';
        this.clearSelection();
      }
      this.loadOfferSets();
    }
  }

  /**
   * Validates the action envelope before reporting confirmed outcomes.
   */
  private presentProtectedResult(operation: ProtectedListOperation, result: unknown): void
  {
    let body = result;
    if (this.isRecord(body) && 'error' in body) {
      body = body.error;
    }
    if (this.isProtectedResult(body, operation)) {
      this.presentProtectedSuccess(body, operation);
      this.presentProtectedFailures(body);
    } else {
      this.showProtectionMessage('The action result could not be confirmed. The list will refresh; check saved state before retrying.');
    }
  }

  /**
   * Narrows unknown response data before field access.
   */
  private isRecord(value: unknown): value is Record<string, unknown>
  {
    const isRecord = value !== null && typeof value === 'object' && !Array.isArray(value);

    return isRecord;
  }

  /**
   * Accepts only nonnegative safe integer counters from action responses.
   */
  private isCount(value: unknown): value is number
  {
    const valid = typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

    return valid;
  }

  /**
   * Requires validated response fields before checking result consistency.
   */
  private isProtectedResult(value: unknown, operation: ProtectedListOperation): value is ProtectedOfferSetBulkActionResponse
  {
    let valid = false;
    if (this.hasProtectedResultFields(value, operation)) {
      valid = this.hasConsistentResultCounts(value);
    }

    return valid;
  }

  /**
   * Validates response identity, target IDs and failure collections against the submitted action.
   */
  private hasProtectedResultFields(value: unknown, operation: ProtectedListOperation): value is ProtectedOfferSetBulkActionResponse
  {
    let valid = false;
    if (this.isRecord(value)) {
      const ids = operation.request.offerSetIds;
      valid = value.accountId === operation.request.accountId && value.action === operation.request.action
        && value.requestedCount === ids.length && this.isCount(value.successCount) && value.successCount <= ids.length
        && this.isCount(value.failureCount) && this.isCount(value.skippedCount) && this.isCount(value.processedCount)
        && typeof value.status === 'string' && protectedResultStatuses.includes(value.status)
        && typeof value.processedIdsTruncated === 'boolean'
        && Array.isArray(value.processedIds) && value.processedIds.every((id) => ids.includes(id))
        && Array.isArray(value.failures) && value.failures.every((failure) => this.isItemFailure(failure, ids))
        && Array.isArray(value.batchFailures) && value.batchFailures.every((failure) => this.isBatchFailure(failure, ids));
    }

    return valid;
  }

  /**
   * Rejects contradictory counters or statuses before showing action completion.
   */
  private hasConsistentResultCounts(response: ProtectedOfferSetBulkActionResponse): boolean
  {
    const protectedCount = response.failures.filter((failure) => failure.code === protectedOfferSetCode).length;
    const accountedCount = response.successCount + response.failureCount + response.skippedCount;
    let valid = accountedCount <= response.requestedCount
      && response.processedCount === accountedCount - protectedCount
      && response.failures.length === response.failureCount
      && new Set(response.processedIds).size === response.processedIds.length;
    if (response.processedIdsTruncated) {
      valid = valid && response.processedIds.length <= response.successCount;
    } else {
      valid = valid && response.processedIds.length === response.successCount;
    }
    if (response.status === 'success') {
      valid = valid && accountedCount === response.requestedCount && response.failureCount === 0
        && response.batchFailures.length === 0;
    }
    if (response.status === 'failed') {
      valid = valid && response.successCount === 0;
    }
    if (response.status === 'partial_success') {
      valid = valid && response.successCount > 0 && (response.failureCount > 0 || response.batchFailures.length > 0);
    }

    return valid;
  }

  /**
   * Requires an item failure to identify a submitted target and valid failure details.
   */
  private isItemFailure(value: unknown, ids: number[]): boolean
  {
    const valid = this.isRecord(value) && typeof value.offerSetId === 'number' && ids.includes(value.offerSetId)
      && typeof value.code === 'string' && typeof value.message === 'string'
      && (value.stage === undefined || typeof value.stage === 'string');

    return valid;
  }

  /**
   * Validates processing failures independently of per-item deletion failures.
   */
  private isBatchFailure(value: unknown, ids: number[]): boolean
  {
    const valid = this.isRecord(value) && this.isCount(value.chunkIndex) && typeof value.code === 'string'
      && typeof value.stage === 'string' && typeof value.message === 'string' && Array.isArray(value.offerSetIds)
      && value.offerSetIds.every((id) => ids.includes(id))
      && (value.component === undefined || typeof value.component === 'string')
      && (value.chunkSize === undefined || this.isCount(value.chunkSize));

    return valid;
  }

  /**
   * Reports confirmed success only when the requested operation has a valid outcome.
   */
  private presentProtectedSuccess(response: OfferSetBulkActionResponse, operation: ProtectedListOperation): void
  {
    if (response.successCount > 0) {
      let detail = `${response.successCount} Offer Set(s) updated successfully.`;
      if (operation.request.action === 'delete') {
        detail = `${response.successCount} Offer Set(s) deleted.`;
      }
      const singleId = operation.request.offerSetIds[0];
      if (!operation.single || response.processedIds.includes(singleId)) {
        this.showProtectionMessage(detail, 'success');
      } else {
        this.showProtectionMessage('The requested deletion could not be confirmed. Check the refreshed list.');
      }
    }
  }

  /**
   * Keeps mutation and post-commit processing failures visible even without item failures.
   */
  private presentProtectedFailures(response: OfferSetBulkActionResponse): void
  {
    const failures = response.failures;
    const batchFailures = response.batchFailures ?? [];
    if (response.successCount === 0 && response.status === 'success') {
      this.showProtectionMessage('No Offer Sets were updated.', 'info');
    }
    if ((response.skippedCount ?? 0) > 0) {
      this.showProtectionMessage(`${response.skippedCount} Offer Set(s) were skipped.`, 'warn');
    }
    if (failures.some((failure) => failure.code === protectedOfferSetCode && failure.stage === 'eligibility')) {
      this.showProtectionMessage(linkedRuleGuidance, 'warn');
    }
    if (response.failureCount > 0) {
      this.showProtectionMessage(`${response.failureCount} Offer Set(s) were not updated.`, 'warn');
    }
    if (batchFailures.some((failure) => failure.stage === 'side_effect')) {
      this.showProtectionMessage('Follow-up processing failed after saved changes. Successful changes remain saved; check the refreshed list.', 'warn');
    }
    if (batchFailures.some((failure) => failure.stage !== 'side_effect')) {
      this.showProtectionMessage('Some batch work failed. The action is incomplete; check the refreshed list before retrying.');
    }
    if (response.status !== 'success' && response.failureCount === 0 && batchFailures.length === 0) {
      this.showProtectionMessage('The action did not complete successfully. Check the refreshed list before retrying.');
    }
  }

  /**
   * Loads the enableNewOfferSetCreationModal MPOP feature flag, defaulting to false on error.
   */
  private loadNewOfferSetCreationModalFeatureFlag(): void {
    const firstElement = 0;
    this.userService.getMpopFeature('enableNewOfferSetCreationModal').pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (feature) => {
        this.enableNewOfferSetCreationModal = feature[firstElement]?.enabled ?? false;
      },
      () => {
        this.enableNewOfferSetCreationModal = false;
      }
    );
  }

  /**
   * Releases subscriptions and prevents pending responses from updating a destroyed view.
   */
  ngOnDestroy(): void
  {
    this.protectionDestroyed = true;
    this.protectedSelectionCancelled.next();
    this.protectedSelectionCancelled.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes debounced search subscription
   */
  initSearchDebounce(): void
  {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged((previous, current) => this.linkedDeletionProtection === false && previous === current),
      takeUntil(this.destroy$)
    ).subscribe((term: string) =>
    {
      this.searchTerm = term;
      this.pagination.page = 1;
      this.loadOfferSets();
    });
  }

  /**
   * Handles search input changes
   */
  onSearchChange(term: string): void
  {
    if (this.linkedDeletionProtection !== false) {
      this.onProtectedSelectionChange();
      this.protectedLoadSequence++;
      this.protectedRowsReady = false;
      this.searchTerm = term;
    }
    this.searchSubject.next(term);
  }

  /**
   * Selects a preset tab and loads data
   */
  selectPreset(tab: PresetTab): void
  {
    this.applyPreset(tab);
    this.updateTabQueryParam(tab.queryValue);
  }

  /**
   * Applies the chosen list preset and reloads its matching rows.
   */
  private applyPreset(tab: PresetTab): void
  {
    this.activePreset = tab.preset;
    this.selectedStatus = tab.status;
    this.selectedCondition = tab.condition;
    this.selectedVisibility = tab.visibility;
    this.pagination.page = this.firstPage;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Handles filter dropdown changes
   */
  onFilterChange(): void
  {
    this.activePreset = '';
    this.pagination.page = this.firstPage;
    this.clearSelection();
    this.loadOfferSets();
    this.updateTabQueryParam(null);
  }

  /**
   * Returns the inventory count for a preset tab.
   */
  getPresetCount(preset: string): number {
    return this.counts.presets[preset] ?? 0;
  }

  /**
   * Returns the count for a visibility option under the selected status and condition.
   */
  getVisibilityCount(visibility: string | undefined): number {
    if (!visibility) {
      return 0;
    }

    return this.counts.visibility[visibility] ?? 0;
  }

  /**
   * Supplies the status facet count used by list filters.
   */
  getStatusCount(status: string | undefined): number
  {
    let count = 0;

    if (status) {
      count = this.counts.status?.[status] ?? 0;
    }

    return count;
  }

  /**
   * Supplies the vehicle-condition facet count used by list filters.
   */
  getConditionCount(condition: string | undefined): number
  {
    let count = 0;

    if (condition) {
      count = this.counts.condition?.[condition] ?? 0;
    }

    return count;
  }

  /**
   * Handles pagination page change
   */
  onPageChange(event: any): void {
    this.pagination.page = (event.page + 1);
    this.pagination.pageSize = event.rows;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Loads offer sets from the API
   */
  loadOfferSets(): void
  {
    if (this.linkedDeletionProtection === false) {
      this.loadOfferSetsWithoutProtection();
    }
    if (this.linkedDeletionProtection === true) {
      this.loadProtectedOfferSets();
    }
    if (this.linkedDeletionProtection === null) {
      this.isLoading = true;
    }
  }

  /**
   * Preserves the original list loading and enrichment flow when protection is disabled.
   */
  private loadOfferSetsWithoutProtection(): void {
    this.isLoading = true;
    this.offerSetListService.getOfferSetList(
      this.accountId,
      this.activePreset,
      this.selectedStatus,
      this.selectedCondition,
      this.selectedVisibility,
      this.searchTerm,
      this.pagination.page,
      this.pagination.pageSize
    ).subscribe(
      (response) => {
        this.items = response.items.map((item) => ({
          ...item,
          isMissingMakeOrModel: this.computeIsMissingMakeOrModel(item)
        }));
        this.pagination = response.pagination;
        this.counts = response.counts ?? {presets: {}, visibility: {}};
        this.isLoading = false;

        this.publicOffersByOfferSetId = {};
        const offerSetIds = this.items
          .map((item) => Number(item.id))
          .filter((id) => Number.isFinite(id) && id > 0);

        if (offerSetIds.length > 0) {
          this.offerSetPublicOffersService.getPublicOffersByOfferSetIds(offerSetIds).subscribe(
            (publicOffers) => {
              this.publicOffersByOfferSetId = this.buildPublicOffersLookup(publicOffers);
            },
            () => {
              this.publicOffersByOfferSetId = {};
            }
          );
        }

        this.offerRegistryService.callOfferRegistryService(this.accountId).subscribe(
          () => {
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to sync Offer Sets.',
              life: 5000
            });
          }
        );
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Offer Sets.',
          life: 5000
        });
        this.isLoading = false;
      }
    );
  }

  /**
   * Build a lookup of public offers by offer set ID
   */
  private buildPublicOffersLookup(publicOffers: OfferSetPublicOffersDto[]): Record<number, OfferSetPublicOffersDto> {
    const lookup: Record<number, OfferSetPublicOffersDto> = {};

    publicOffers.forEach((dto) => {
      const standardOfferSetId = dto.standardOfferSetId ?? null;
      if (standardOfferSetId !== null && standardOfferSetId > 0) {
        lookup[standardOfferSetId] = dto;
      }
    });

    return lookup;
  }

  /**
   * Checks if the offer sets has public rules associated with it to mark with Public tag
   */
  hasActivePublicOffer(itemId: number): boolean {
    const publicOffers = this.publicOffersByOfferSetId[itemId] ?? null;
    let hasActiveOffer = false;

    if (publicOffers !== null) {
      hasActiveOffer = publicOffers.leaseOfferIsPublic
        || publicOffers.alternateLeaseIsPublic
        || publicOffers.aprOfferIsPublic
        || publicOffers.financeOfferIsPublic;
    }

    return hasActiveOffer;
  }

  /**
   * Toggles selection of all visible items
   */
  toggleSelectAll(event: { checked: boolean }): void
  {
    this.onProtectedSelectionChange();
    this.selectAllAcrossPages = false;

    const selectableItems = this.getSelectableItems(this.items);
    this.allSelected = event.checked;

    if (this.allSelected) {
      this.selectedItems = [...selectableItems];
    } else {
      this.selectedItems = [];
    }
  }

  /**
   * Clears all selections
   */
  clearSelection(): void
  {
    this.onProtectedSelectionChange();
    this.selectedItems = [];
    this.allSelected = false;
    this.selectAllAcrossPages = false;
  }

  /**
   * Checks if a specific item is selected
   */
  isItemSelected(item: OfferSetListItem): boolean {
    return this.selectedItems.some(selected => selected.id === item.id);
  }

  /**
   * Toggles selection of a single item
   */
  toggleItemSelection(item: OfferSetListItem): void
  {
    if (this.linkedDeletionProtection === false || (this.items.includes(item) && this.isSelectable(item))) {
      this.toggleSelectableItem(item);
    }
  }

  /**
   * Ignores protected or unavailable rows when changing selection.
   */
  private toggleSelectableItem(item: OfferSetListItem): void
  {
    this.onProtectedSelectionChange();
    this.selectAllAcrossPages = false;
    if (this.isSelectable(item)) {
      let selectedItems = [...this.selectedItems, item];
      if (this.isItemSelected(item)) {
        selectedItems = this.selectedItems.filter((selected) => selected.id !== item.id);
      }
      this.selectedItems = selectedItems;
      const selectableItems = this.getSelectableItems(this.items);
      this.allSelected = selectableItems.length > 0 && this.selectedItems.length === selectableItems.length;
    }
  }

  /**
   * Marks selection as all results across pages
   */
  selectAllAcross(): void
  {
    if (this.linkedDeletionProtection === false || this.protectedRowsReady) {
      this.selectAllEligibleAcross();
    }
  }

  /**
   * Stages across-page selection without treating protected rows as eligible.
   */
  private selectAllEligibleAcross(): void
  {
    this.onProtectedSelectionChange();
    this.selectAllAcrossPages = true;
    this.allSelected = true;

    const selectableItems = this.getSelectableItems(this.items);
    this.selectedItems = [...selectableItems];
  }

  /**
   * Returns true when the offer set is missing a make or model, blocking activation.
   */
  private computeIsMissingMakeOrModel(item: OfferSetListItem): boolean {
    const makeId = Number(item.data.makeId);
    const modelId = Number(item.data.modelId);
    const makeIsMissing = !makeId || makeId <= 0;
    const modelIsMissing = !modelId || modelId <= 0;

    return makeIsMissing || modelIsMissing;
  }

  /**
   * Toggles the active status of a single offer set
   */
  toggleStatus(item: OfferSetListItem): void {
    const newAction = item.data.active ? 'inactivate' : 'activate';
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: newAction,
      offerSetIds: [item.id]
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      () => {
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update Offer Set status.',
          life: 5000
        });
      }
    );
  }

  /**
   * Builds the edit page URL so native link navigation remains available.
   */
  getOfferSetEditorUrl(item: OfferSetListItem): string | null {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;
    const offerSetId = item?.id;
    const condition = this.resolveEditorCondition(item.data.newUsed);
    let url: string | null = null;

    if (adminDomain && accountId && offerSetId && condition) {
      url = `http://${adminDomain}/${condition}-specials/${accountId}#/specials/edit/${offerSetId}`;
    }

    return url;
  }

  /**
   * Navigates to the edit page for a matched (public-source) offer set.
   */
  openMatchedOfferSetEditor(matchedOfferSetId: number | null | undefined): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;
    const offerSetId = matchedOfferSetId ?? null;

    if (!adminDomain || !accountId || !offerSetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the editor.',
        life: 4000
      });

      return;
    }

    const url = `http://${adminDomain}/new-specials/${accountId}#/specials/edit/${offerSetId}`;

    window.location.href = url;
  }

  /**
   * Opens the disclosure dialog with the item's fullDisclosure content
   */
  openDisclosure(item: OfferSetListItem): void {
    const title = item?.data?.displayTitle || 'Disclosure';
    const content = item?.data?.fullDisclosure || '';

    this.disclosureTitle = title;
    this.disclosureHtml = this.sanitizer.bypassSecurityTrustHtml(content);
    this.disclosureDialogVisible = true;
  }

  /**
   * Closes the disclosure dialog
   */
  closeDisclosure(): void {
    this.disclosureDialogVisible = false;
    this.disclosureTitle = '';
    this.disclosureHtml = '';
  }

  /**
   * Opens delete confirmation for a single offer set
   */
  openDeleteConfirmation(item: OfferSetListItem): void
  {
    if (this.linkedDeletionProtection === false) {
      this.openDeleteConfirmationWithoutProtection(item);
    }
    if (this.linkedDeletionProtection === true) {
      this.beginProtectedSingleDelete(item);
    }
    if (this.linkedDeletionProtection === null) {
      this.showProtectionMessage(deletionProtectionUnavailable);
    }
  }

  /**
   * Preserves the original single-delete confirmation when protection is disabled.
   */
  private openDeleteConfirmationWithoutProtection(item: OfferSetListItem): void {
    this.confirmationService.confirm({
      key: 'deleteOfferSet',
      defaultFocus: 'none',
      message: `Are you sure you want to delete "${item.data.displayTitle}"? Deleting Offer Sets may also remove associated rules, if present.`,
      header: 'Delete Offer Set',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        this.deleteOfferSet(item);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Deletes a single offer set
   */
  deleteOfferSet(item: OfferSetListItem): void
  {
    if (this.linkedDeletionProtection === false) {
      this.deleteOfferSetWithoutProtection(item);
    }
    if (this.linkedDeletionProtection === true) {
      this.beginProtectedSingleDelete(item);
    }
    if (this.linkedDeletionProtection === null) {
      this.showProtectionMessage(deletionProtectionUnavailable);
    }
  }

  /**
   * Preserves legacy single-delete completion and error handling when protection is disabled.
   */
  private deleteOfferSetWithoutProtection(item: OfferSetListItem): void {
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: 'delete',
      offerSetIds: [item.id]
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Offer Set deleted.',
          life: 3000
        });
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete Offer Set.',
          life: 5000
        });
      }
    );
  }

  /**
   * Applies the selected bulk action to selected items
   */
  applyBulkAction(): void
  {
    if (this.linkedDeletionProtection === false) {
      this.applyBulkActionWithoutProtection();
    }
    if (this.linkedDeletionProtection === true) {
      this.beginProtectedBulkAction();
    }
    if (this.linkedDeletionProtection === null) {
      this.showProtectionMessage(deletionProtectionUnavailable);
    }
  }

  /**
   * Preserves the original selected-action flow when protection is disabled.
   */
  private applyBulkActionWithoutProtection(): void {
    let proceed = true;

    if (!this.selectedBulkAction) {
      proceed = false;
    }

    if (proceed && !this.selectAllAcrossPages && this.selectedItems.length === 0) {
      proceed = false;
    }

    if (proceed) {
      if (this.selectedBulkAction === 'delete') {
        this.openBulkDeleteConfirmation();
      } else if (this.selectAllAcrossPages) {
        this.executeBulkActionAcrossAll();
      } else {
        this.executeBulkAction();
      }
    }
  }

  /**
   * Opens bulk delete confirmation dialog
   */
  openBulkDeleteConfirmation(): void
  {
    if (this.linkedDeletionProtection === false) {
      this.openBulkDeleteConfirmationWithoutProtection();
    }
    if (this.linkedDeletionProtection === true) {
      this.beginProtectedBulkAction();
    }
    if (this.linkedDeletionProtection === null) {
      this.showProtectionMessage(deletionProtectionUnavailable);
    }
  }

  /**
   * Preserves the original bulk-delete confirmation when protection is disabled.
   */
  private openBulkDeleteConfirmationWithoutProtection(): void {
    this.confirmationService.confirm({
      key: 'bulkDeleteOfferSets',
      defaultFocus: 'none',
      message: `Are you sure you want to delete ${this.getSelectedCount()} Offer Set(s)? Deleting Offer Sets may also remove associated rules, if present.`,
      header: 'Delete Offer Sets',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        if (this.selectAllAcrossPages) {
          this.executeBulkActionAcrossAll();
        } else {
          this.executeBulkAction();
        }
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Executes the bulk action API call
   */
  executeBulkAction(): void
  {
    if (this.linkedDeletionProtection === false) {
      this.executeBulkActionWithoutProtection();
    }
    if (this.linkedDeletionProtection === true) {
      this.beginProtectedBulkAction();
    }
    if (this.linkedDeletionProtection === null) {
      this.showProtectionMessage(deletionProtectionUnavailable);
    }
  }

  /**
   * Preserves baseline bulk submission and result handling when protection is disabled.
   */
  private executeBulkActionWithoutProtection(): void {
    const selectedIds = this.selectedItems.map(item => item.id);
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: this.selectedBulkAction,
      offerSetIds: selectedIds
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${response.successCount} Offer Set(s) updated successfully.`,
          life: 3000
        });
        if (response.failureCount > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: `${response.failureCount} Offer Set(s) failed to update.`,
            life: 5000
          });
        }
        this.selectedBulkAction = '';
        this.clearSelection();
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to execute bulk action.',
          life: 5000
        });
      }
    );
  }

  /**
   * Executes bulk action when all results across pages are selected
   */
  private executeBulkActionAcrossAll(): void {
    this.isLoading = true;
    this.fetchAllIdsAcrossPages()
      .subscribe({
        next: (allIds) => {
          const request: OfferSetBulkActionRequest = {
            accountId: this.accountId,
            action: this.selectedBulkAction,
            offerSetIds: allIds
          };

          this.offerSetListService.executeBulkAction(request).subscribe(
            (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `${response.successCount} Offer Set(s) updated successfully.`,
                life: 3000
              });
              if (response.failureCount > 0) {
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Warning',
                  detail: `${response.failureCount} Offer Set(s) failed to update.`,
                  life: 5000
                });
              }
              this.selectedBulkAction = '';
              this.clearSelection();
              this.loadOfferSets();
            },
            () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to execute bulk action.',
                life: 5000
              });
              this.isLoading = false;
            }
          );
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to gather all Offer Set IDs for bulk action.',
            life: 5000
          });
          this.isLoading = false;
        }
      });
  }

  /**
   * Returns the number of selected items, accounting for select all across pages
   */
  private getSelectedCount(): number {
    let count = this.selectedItems.length;

    if (this.selectAllAcrossPages) {
      let total = 0;

      if (this.pagination && typeof this.pagination.totalItems === 'number') {
        total = this.pagination.totalItems;
      }

      count = total;
    }

    return count;
  }

  /**
   * Collects all Offer Set IDs across all pages using current filters
   */
  private fetchAllIdsAcrossPages() {
    const pageSize = 100;
    const ids: number[] = [];

    const loadPage = (page: number, next: (done: boolean) => void) => {
      this.offerSetListService
        .getOfferSetList(
          this.accountId,
          this.activePreset,
          this.selectedStatus,
          this.selectedCondition,
          this.selectedVisibility,
          this.searchTerm,
          page,
          pageSize
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (resp) => {
            const pageIds = (resp.items || [])
              .filter((item) => !item.isShared)
              .map(i => i.id);
            ids.push(...pageIds);
            const totalPages = resp.pagination?.totalPages || 0;
            const done = page >= totalPages;
            next(done);
          },
          error: () => next(true)
        });
    };

    return new Observable<number[]>((subscriber) => {
      let current = 1;
      const iterate = () => {
        loadPage(current, (done) => {
          if (done) {
            subscriber.next(ids);
            subscriber.complete();
          } else {
            current += 1;
            iterate();
          }
        });
      };
      iterate();
    });
  }

  /**
   * Navigates to the inventory view
   */
  toggleInventoryMenu(event: MouseEvent, menu: Menu, button: HTMLButtonElement): void {
    const buttonWidth = `${button.offsetWidth}px`;
    menu.style = {width: buttonWidth, minWidth: buttonWidth};
    menu.toggle(event);
  }

  /**
   * Opens inventory filtered to the selected offer set.
   */
  viewInventory(condition: 'new' | 'used'): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;

    if (!adminDomain || !accountId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open inventory.',
        life: 4000
      });
    } else {
      let specialsPath = 'new-specials';
      if (condition === 'used') {
        specialsPath = 'used-specials';
      }
      const url = `http://${adminDomain}/${specialsPath}/${accountId}#/specials/inventory`;

      window.location.href = url;
    }
  }

  /**
   * Opens the Build New Offer Set dialog and loads make/model options
   */
  buildNewOfferSet(): void {
    this.buildDialogVisible = true;
    this.loadVehicleOptions();
  }

  /**
   * Builds the year options for the offer-set condition.
   */
  private buildYearOptions(condition: string): number[] {
    const currentYear = new Date().getFullYear();
    const finalYear = currentYear + this.yearOptionsFutureOffset;
    let startYear = this.newVehicleStartYear;
    if (condition.toLowerCase() === this.stockConditionUsed) {
      startYear = this.usedVehicleStartYear;
    }

    return Array.from(
      {length: finalYear - startYear + 1},
      (_, index) => finalYear - index
    );
  }

  /**
   * Loads the makes and models used to populate the manual create sub-form
   */
  private loadVehicleOptions(): void {
    this.vehicleService.getAllMakes().pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (makes) => {
        this.makes = makes || [];
      },
      () => {
        this.makes = [];
      }
    );
    this.vehicleService.getAllModels().pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (models) => {
        this.models = models || [];
      },
      () => {
        this.models = [];
      }
    );
  }

  /**
   * Closes the Build dialog and opens the Manually Create Offer Set dialog
   */
  selectManualCreate(): void {
    this.buildDialogVisible = false;
    this.manualDialogVisible = true;
  }

  /**
   * Closes the Build dialog and opens the Enter from Stock dialog
   */
  selectEnterFromStock(): void {
    this.buildDialogVisible = false;
    this.stockDialogVisible = true;
  }

  /**
   * Returns from the Manually Create Offer Set dialog back to the Build dialog
   */
  backToBuildDialog(): void {
    this.manualDialogVisible = false;
    this.buildDialogVisible = true;
  }

  /**
   * Returns from the Enter from Stock dialog back to the Build dialog
   */
  backToBuildDialogFromStock(): void {
    this.stockDialogVisible = false;
    this.buildDialogVisible = true;
  }

  /**
   * Filters the model list to the selected make and resets the model selection
   */
  onBuildMakeChange(): void {
    const selectedMakeId = this.newOfferSetMakeId;
    this.filteredModels = this.models.filter((model) => model.makeId === selectedMakeId);
    this.newOfferSetModelId = null;
  }

  /**
   * Closes the Build New Offer Set dialog and clears the form state
   */
  closeBuildDialog(): void {
    this.buildDialogVisible = false;
    this.resetBuildForm();
  }

  /**
   * Closes the Manually Create Offer Set dialog and clears the form state
   */
  closeManualDialog(): void {
    this.manualDialogVisible = false;
    this.resetBuildForm();
  }

  /**
   * Closes the Enter from Stock dialog and clears the form state
   */
  closeStockDialog(): void {
    this.stockDialogVisible = false;
    this.resetBuildForm();
  }

  /**
   * Resets the manual create sub-form to its default state
   */
  private resetBuildForm(): void {
    this.newOfferSetCondition = this.conditionNew;
    this.newOfferSetYear = null;
    this.newOfferSetMakeId = null;
    this.newOfferSetModelId = null;
    this.newOfferSetTrim = '';
    this.newOfferSetStockNumber = '';
    this.filteredModels = [];
  }

  /**
   * Validates and submits stock creation once per user action.
   */
  enterFromStock(): void {
    if (!this.isCreatingFromStock) {
      const request = this.buildFromStockRequest();

      if (!request) {
        this.messageService.add({
          severity: 'error',
          summary: 'Create Error',
          detail: 'Missing data required to create the Offer Set from stock.',
          life: 4000
        });
      } else {
        this.submitCreateAdFromStock(request);
      }
    }
  }

  /**
   * Reproduces the legacy close, notification, and request sequence.
   */
  private submitCreateAdFromStock(request: CreateAdFromStockRequest): void {
    this.isCreatingFromStock = true;
    this.stockDialogVisible = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Creating Offer Set',
      detail: 'Creating new offer set from stock number...',
      life: 4000
    });
    this.offerSetListService.createAdFromStock(
      request.accountId,
      request.stockNumber,
      request.expectedCondition
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response) => {
        this.handleCreateAdFromStockResponse(response, request.stockNumber);
      },
      () => {
        this.isCreatingFromStock = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Create Error',
          detail: 'Unable to create offer set from stock number. Please contact support@dealerteamwork.com.',
          life: 4000
        });
      }
    );
  }

  /**
   * Builds the from-stock request from the modal input, or null when required data is missing
   */
  private buildFromStockRequest(): CreateAdFromStockRequest | null {
    const accountId = this.accountId;
    const stockNumber = (this.newOfferSetStockNumber || '').trim();
    const expectedCondition = this.resolveExpectedStockCondition();
    let request: CreateAdFromStockRequest | null = null;

    if (accountId && stockNumber && this.isAllowedStockCondition(expectedCondition)) {
      request = {
        accountId: accountId,
        stockNumber: stockNumber,
        expectedCondition: expectedCondition
      };
    }

    return request;
  }

  /**
   * Preserves the legacy endpoint contract without requiring another modal input.
   * The legacy UI inferred this value from its New/Used page context. The combined
   * list uses the active condition when available and New as the fallback.
   */
  private resolveExpectedStockCondition(): string {
    let expectedCondition = this.stockConditionNew;
    if (this.isAllowedStockCondition(this.selectedCondition)) {
      expectedCondition = this.selectedCondition;
    }

    return expectedCondition;
  }

  /**
   * Separates business errors in the HTTP-200 envelope from successful creation.
   */
  private handleCreateAdFromStockResponse(response: CreateAdFromStockResponse, stockNumber: string): void {
    this.isCreatingFromStock = false;
    if (!response.success || !response.result) {
      this.showCreateAdFromStockErrors(response.errors);
    } else {
      const editorCondition = this.resolveEditorCondition(response.result.newUsed);
      if (!editorCondition) {
        this.showCreateAdFromStockErrors(['Created offer set has an unsupported vehicle condition.']);
      } else if (response.result.expectedConditionMatches) {
        this.redirectToStockAdEditor(response.result.adId, editorCondition);
      } else {
        this.handleExpectedConditionMismatch(response.result.adId, stockNumber, editorCondition);
      }
    }
  }

  /**
   * Displays every business error returned by the existing endpoint.
   */
  private showCreateAdFromStockErrors(errors: string[]): void {
    let errorMessages = errors;
    if (errorMessages.length === 0) {
      errorMessages = ['Failed to create the Offer Set from stock.'];
    }
    errorMessages.forEach((detail) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Create Error',
        detail: detail,
        life: 4000
      });
    });
  }

  /**
   * Validates the expected condition against the new/used allow-list
   */
  private isAllowedStockCondition(condition: string): boolean {
    const allowedConditions = [this.stockConditionNew, this.stockConditionUsed];

    return allowedConditions.includes(condition);
  }

  /**
   * Maps the inventory newUsed value to the condition used for the editor redirect
   */
  private resolveEditorCondition(newUsed: string): string | null {
    const normalized = (newUsed || '').trim().toLowerCase();
    const newValues = ['n', this.stockConditionNew];
    const usedValues = ['u', this.stockConditionUsed];
    let condition: string | null = null;

    if (newValues.includes(normalized)) {
      condition = this.stockConditionNew;
    }
    if (usedValues.includes(normalized)) {
      condition = this.stockConditionUsed;
    }

    return condition;
  }

  /**
   * Notifies the user that the vehicle was created under the opposite condition, then redirects after a delay
   */
  private handleExpectedConditionMismatch(adId: number, stockNumber: string, actualCondition: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Vehicle Condition Updated',
      detail: `The vehicle associated with stock number ${stockNumber} was created with the "${actualCondition}" vehicle condition. Redirecting to the editor…`,
      life: this.mismatchRedirectDelayMs
    });
    setTimeout(() => {
      this.redirectToStockAdEditor(adId, actualCondition);
    }, this.mismatchRedirectDelayMs);
  }

  /**
   * Redirects a stock-created legacy ad through its persisted condition route.
   */
  private redirectToStockAdEditor(adId: number, condition: string): void {
    const accountId = this.accountId;
    if (!accountId || !adId || !this.isAllowedStockCondition(condition)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the stock-created Offer Set.',
        life: 4000
      });
    } else {
      const url = `${window.location.origin}/${condition}-specials/${accountId}#/specials/edit/${adId}`;

      window.location.href = url;
    }
  }

  /**
   * Submits the manual create form to the create endpoint and redirects on success
   */
  manuallyCreateOfferSet(): void {
    const request = this.buildCreateRequest();

    if (!request) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to create the Offer Set.',
        life: 4000
      });
    } else {
      this.submitCreateOfferSet(request);
    }
  }

  /**
   * Sends the validated manual create request and redirects on success
   */
  private submitCreateOfferSet(request: OfferSetCreateRequest): void {
    const condition = request.vehicle.condition;
    this.offerSetListService.createOfferSet(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response) => {
        this.manualDialogVisible = false;
        this.redirectToOfferSetEditor(response.id, condition);
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Create Error',
          detail: 'Failed to create the Offer Set.',
          life: 4000
        });
      }
    );
  }

  /**
   * Builds the create request from the manual form, or null when required data is missing
   */
  private buildCreateRequest(): OfferSetCreateRequest | null {
    const accountId = this.accountId;
    const condition = this.newOfferSetCondition;
    const year = this.newOfferSetYear;
    const selectedMake = this.makes.find((make) => make.id === this.newOfferSetMakeId) ?? null;
    const selectedModel = this.models.find((model) => model.id === this.newOfferSetModelId) ?? null;
    let request: OfferSetCreateRequest | null = null;

    if (accountId && condition && year && selectedMake && selectedModel) {
      const trim = this.newOfferSetTrim || null;
      request = {
        accountId: accountId,
        vehicle: {
          year: year,
          make: selectedMake.displayName,
          model: selectedModel.name,
          modelId: selectedModel.id,
          trim: trim,
          condition: condition
        }
      };
    }

    return request;
  }

  /**
   * Redirects to the legacy editor for the newly created offer set
   */
  private redirectToOfferSetEditor(offerSetId: number, condition: string): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;

    if (!adminDomain || !accountId || !offerSetId || !condition) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the editor.',
        life: 4000
      });
    } else {
      const url = `http://${adminDomain}/new-specials/${accountId}#/specials/edit/${offerSetId}`;

      window.location.href = url;
    }
  }
}
